import { Busker, Comment, Place, Post, Subscription } from "@/types/type";
import { supabase } from "@/utils/supabase";
import geohash from "ngeohash";

export const getCommentsForPlace = async (
  placeId: string,
  limit: number,
  offset: number,
) => {
  console.log(`Fetching comments for place: ${placeId}`);
  const { data, error } = await supabase
    .from("comments")
    .select(`*,profile:profiles(*)`)
    .eq("place_id", placeId)
    .order("created_at", { ascending: false })
    .range(offset, offset + limit - 1)
    .returns<Comment[]>();

  if (error) {
    console.error("Error fetching comments:", error);
    return [];
  }

  return data;
};

export const getCommentsForPost = async (
  postId: string,
  limit: number,
  offset: number,
) => {
  console.log(`Fetching comments for post: ${postId}`);
  const { data, error } = await supabase
    .from("comments")
    .select(`*,profile:profiles(*)`)
    .eq("post_id", postId)
    .order("created_at", { ascending: false })
    .range(offset, offset + limit - 1)
    .returns<Comment[]>();

  if (error) {
    console.error("Error fetching comments:", error);
    return [];
  }

  return data;
};

export const submitComment = async (comment: Comment) => {
  console.log("Submitting comment:", comment);
  const { data, error } = await supabase
    .from("comments")
    .upsert(comment)
    .select()
    .returns<Comment>()
    .single();
  console.log(data);
  if (error) {
    console.error("Error submitting comment:", error);
  }
  return data;
};

export const fetchPlacesByCoordinates = async (
  latitude: number,
  longitude: number,
  latitudeDelta: number,
  longitudeDelta: number,
) => {
  console.log(
    "Fetching places for coordinates: ",
    latitude,
    longitude,
    latitudeDelta,
    longitudeDelta,
  );
  const minLat = latitude - latitudeDelta;
  const maxLat = latitude + latitudeDelta;
  const minLng = longitude - longitudeDelta;
  const maxLng = longitude + longitudeDelta;
  var geohashesList: string[] = [];
  for (let precision = 1; precision <= 9; precision++) {
    const geohashes = geohash.bboxes(minLat, minLng, maxLat, maxLng, precision);
    if (geohashes.length > 6) {
      geohashesList = geohashes;
      break;
    }
  }

  console.log("Geohash range:", geohashesList);
  geohashesList = geohashesList.map((hash) => hash + "%");

  const { data, error } = await supabase
    .from("places")
    .select(`*,subscriptions(*)`)
    .likeAnyOf("geohash", geohashesList)
    .order("last_activity", { ascending: false })
    .limit(15);

  if (error) {
    console.error("Error fetching places:", error);
    return [];
  }

  if (!data) return [];

  return data.map(
    (place): Place => ({
      id: place.id,
      created_at: place.created_at,
      title: place.title,
      description: place.description,
      legal_notices: place.legal_notices,
      latitude: place.latitude,
      longitude: place.longitude,
      street: place.street,
      city: place.city,
      country: place.country,
      cover_url: place.cover_url,
      owner_id: place.owner_id,
      moderated: place.moderated,
      owner: place.owner,
      subscribed: place.subscriptions.length > 0,
      comments_num: place.comments_num,
    }),
  );
};

export const fetchPostsSubscribedPlaces = async (
  limit: number,
  offset: number,
) => {
  console.log(`Fetching posts: ${limit} ${offset}`);
  const session = await supabase.auth.getSession();
  const userId = session.data.session?.user?.id;
  const posts = await supabase
    .from("posts")
    .select(`*,busker:buskers(*),place:places!inner(*,subscriptions!inner(*))`)
    .eq("place.subscriptions.owner_id", userId)
    .range(offset, offset + limit - 1)
    .order("created_at", { ascending: false })
    .returns<Post[]>();

  if (posts.error) {
    throw posts.error;
  }
  console.log(posts);

  return posts.data;
};

export const fetchAds = async (limit: number, offset: number) => {
  console.log(`Fetching ads: ${limit} ${offset}`);
  const session = await supabase.auth.getSession();
  const userId = session.data.session?.user?.id;
  const posts = await supabase
    .from("posts")
    .select(`*,suggestion:suggestions!inner(*)`)
    .eq("suggestions.type", "ad")
    .eq("suggestions.owner_id", userId)
    .is("suggestions.viewed_at",  null)
    .range(offset, offset + limit - 1)
    .order("created_at", { ascending: true })
    .returns<Post[]>();

  if (posts.error) {
    throw posts.error;
  }
  console.log(posts);

  return posts.data;
};

export const fetchSuggestions = async (limit: number, offset: number) => {
  console.log(`Fetching suggestions: ${limit} ${offset}`);
  const session = await supabase.auth.getSession();
  const userId = session.data.session?.user?.id;
  const posts = await supabase
    .from("posts")
    .select(`*,suggestion:suggestions!inner(*)`)
    .eq("suggestions.type", "suggestion")
    .eq("suggestions.owner_id", userId)
    .is("suggestions.viewed_at",  null)
    .range(offset, offset + limit - 1)
    .order("created_at", { ascending: false })
    .returns<Post[]>();

  if (posts.error) {
    throw posts.error;
  }
  console.log(posts);

  return posts.data;
};

export const fetchPostsSubscribedBuskers = async (
  limit: number,
  offset: number,
) => {
  console.log(`Fetching posts for subscribed buskers: ${limit} ${offset}`);
  const session = await supabase.auth.getSession();
  const userId = session.data.session?.user?.id;

  const posts = await supabase
    .from("posts")
    .select(`*,busker:buskers!inner(*,subscriptions!inner(*)),place:places(*)`)
    .eq("busker.subscriptions.owner_id", userId)
    .range(offset, offset + limit - 1)
    .order("created_at", { ascending: false })
    .returns<Post[]>();

  if (posts.error) {
    throw posts.error;
  }
  console.log(posts);

  return posts.data ?? [];
};

export const fetchSubscriptions = async (limit: number, offset: number) => {
  console.log(`Fetching subscriptions: ${limit} ${offset}`);
  const session = await supabase.auth.getSession();
  const userId = session.data.session?.user?.id;
  const posts = await supabase
    .from("subscriptions")
    .select(`*,busker:buskers(*),place:places(*)`)
    .eq("owner_id", userId)
    .range(offset, offset + limit - 1)
    .not("last_activity", "is", null)
    .order("last_activity", { ascending: false })
    .returns<Subscription[]>();

  if (posts.error) {
    throw posts.error;
  }
  console.log(posts);
  if (posts.data) {
    posts.data = posts.data.map((subscription) => ({
      ...subscription,
      busker: subscription.busker
        ? { ...subscription.busker, subscribed: true }
        : undefined,
      place: subscription.place
        ? { ...subscription.place, subscribed: true }
        : undefined,
    }));
  }
  return posts.data;
};

export const fetchPostsForPlace = async (
  id: string,
  limit: number,
  offset: number,
) => {
  console.log(`Fetching posts: ${limit} ${offset}`);
  const posts = await supabase
    .from("posts")
    .select(`*,busker:buskers(*),place:places!inner(id)`)
    .eq("place_id", id)
    .range(offset, offset + limit - 1)
    .order("when", { ascending: false })
    .returns<Post[]>();

  if (posts.error) {
    throw posts.error;
  }
  console.log(posts);

  return posts.data ?? [];
};

export const fetchPostsForBusker = async (
  id: string,
  limit: number,
  offset: number,
) => {
  console.log(`Fetching posts: ${limit} ${offset}`);
  const posts = await supabase
    .from("posts")
    .select(`*,busker:buskers!inner(id),place:places(*)`)
    .eq("busker_id", id)
    .range(offset, offset + limit - 1)
    .order("when", { ascending: false })
    .returns<Post[]>();

  if (posts.error) {
    throw posts.error;
  }
  console.log(posts);

  return posts.data ?? [];
};

export const fetchPost = async (id: string): Promise<Post | null> => {
  console.log(`Fetching post: ${id}`);
  const post = await supabase
    .from("posts")
    .select(`*,busker:buskers(*),place:places(*)`)
    .eq("id", id)
    .returns<Post>()
    .single();

  if (post.error) {
    throw post.error;
  }
  console.log(post);
  return post.data ?? null;
};

export const fetchPlace = async (id: string) => {
  console.log(`Fetching place: ${id}`);
  const place = await supabase
    .from("places")
    .select(`*`)
    .eq("id", id)
    .returns<Place>()
    .single();

  if (place.error) {
    throw place.error;
  }
  console.log(place);
  return place.data ?? null;
};

export const fetchBusker = async (id: string) => {
  console.log(`Fetching busker: ${id}`);
  const busker = await supabase
    .from("buskers")
    .select(`*`)
    .eq("id", id)
    .returns<Busker>()
    .single();

  if (busker.error) {
    throw busker.error;
  }
  console.log(busker);
  return busker.data ?? null;
};

export const submitPost = async (post: Post) => {
  console.log("Submitting post:", post);
  const { data, error } = await supabase.from("posts").upsert(post);
  if (error) {
    console.error("Error submitting post:", error);
  }
};

export const submitPlace = async (place: Place) => {
  console.log("Submitting place:", place);
  const { data, error } = await supabase.from("places").upsert(place);
  if (error) {
    console.error("Error submitting place:", error);
  }
};

export const fetchBuskers = async (limit: number, offset: number) => {
  console.log(`Fetching buskers: ${limit} ${offset}`);
  const session = await supabase.auth.getSession();
  const { data, error } = await supabase
    .from("buskers")
    .select(`*,subscriptions(*)`)
    .eq("subscriptions.owner_id", session.data.session?.user?.id)
    .is("subscriptions", null)
    .range(offset, offset + limit - 1)
    .order("created_at", { ascending: true });

  if (error) {
    console.error("Error fetching posts:", error);
    return [];
  }
  if (!data) return [];

  return data.map(
    (busker): Busker => ({
      id: busker.id,
      created_at: busker.created_at,
      description: busker.description,
      email: busker.email,
      full_name: busker.full_name,
      profile_image: busker.profile_image,
      owner_id: busker.owner_id,
      subscribed: busker.subscriptions.length > 0,
    }),
  );
};

export const getBusker = async (id: string) => {
  console.log(`Fetching busker: ${id}`);
  const { data, error } = await supabase.from("buskers").select().eq("id", id);

  if (error) {
    console.error("Error fetching busker:", error);
    return [];
  }

  if (!data) return [];

  return data.map(
    (busker): Busker => ({
      id: busker.id,
      created_at: busker.created_at,
      description: busker.description,
      email: busker.email,
      full_name: busker.full_name,
      profile_image: busker.profile_image,
      owner_id: busker.owner_id,
    }),
  );
};

export const getMyBusker = async (id: string) => {
  console.log(`Fetching buskers: ${id}`);
  const { data, error } = await supabase
    .from("buskers")
    .select()
    .eq("profile_owner_id", id)
    .order("created_at", { ascending: true })
    .returns<Busker>()
    .single();

  if (error) {
    console.error("Error fetching busker:", error);
    throw error;
  }

  return data;
};

export const submitBusker = async (busker: Busker) => {
  console.log("Submitting busker:", busker);
  const { subscribed, ...objectToInsert } = busker;
  const { data, error } = await supabase.from("buskers").upsert(objectToInsert);
  if (error) {
    console.error("Error submitting place:", error);
  }
};

export const subscribeToPlace = async (placeId: string) => {
  console.log("Subscribing to place:", placeId);
  const { data, error } = await supabase
    .from("subscriptions")
    .upsert({ place_id: placeId });
  if (error) {
    console.error("Error subscribing to place:", error);
  }
};

export const unsubscribeFromPlace = async (placeId: string) => {
  console.log("Unsubscribing from place:", placeId);
  const session = await supabase.auth.getSession();
  const userId = session.data.session?.user?.id;
  const { data, error } = await supabase
    .from("subscriptions")
    .delete()
    .eq("place_id", placeId)
    .eq("owner_id", userId);
  if (error) {
    console.error("Error unsubscribing from place:", error);
  }
};

export const checkSubscription = async (entity: string, id: string) => {
  console.log("Fetching subscriptions");
  const session = await supabase.auth.getSession();
  const userId = session.data.session?.user?.id;
  var column = "unknown";
  if (entity === "place") {
    column = "place_id";
  } else if (entity === "busker") {
    column = "busker_id";
  }
  const { data, error } = await supabase
    .from("subscriptions")
    .select()
    .eq("owner_id", userId)
    .eq(column, id);
  if (error) {
    console.error("Error fetching subscriptions:", error);
    return false;
  }
  if (data.length > 0) {
    return true;
  }
  return false;
};

export const subscribeToBusker = async (buskerId: string) => {
  console.log("Subscribing to busker:", buskerId);
  const { data, error } = await supabase
    .from("subscriptions")
    .upsert({ busker_id: buskerId });
  if (error) {
    console.error("Error subscribing to busker:", error);
  }
};

export const unsubscribeFromBusker = async (buskerId: string) => {
  console.log("Unsubscribing from busker:", buskerId);
  const session = await supabase.auth.getSession();
  const userId = session.data.session?.user?.id;
  const { data, error } = await supabase
    .from("subscriptions")
    .delete()
    .eq("busker_id", buskerId)
    .eq("owner_id", userId);
  if (error) {
    console.error("Error unsubscribing from busker:", error);
  }
};
